// Cart (parts/Advisor Chat and Cart, parts/Mobile Cart, parts/Overlays and States B): every add-to-cart form posts through the AJAX cart,
// the drawer and the cart page re-render through the Section Rendering API, and the drawer opens on the add (a toast where there is no drawer).
// Without JavaScript the forms post to /cart.

export const fill = (template, values) => String(template ?? '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));

// The forms the cart takes over: POST forms whose action is /cart/add (with or without a locale prefix), unless they opt out with data-ns-no-ajax.
export function isAddForm(form) {
  if (!form || typeof form.getAttribute !== 'function' || form.hasAttribute('data-ns-no-ajax')) return false;
  const action = (form.getAttribute('action') || '').split('?')[0];
  return /\/cart\/add(\.js)?$/.test(action) && (form.getAttribute('method') || '').toLowerCase() === 'post';
}

// What a submit does: forms the cart does not own pass to the browser; a second submit while an add is running is held
// (prevented, not sent again), so a double click or a held Enter never posts natively on top of the AJAX add.
export function submitDecision(form, hasDrawer) {
  if (!hasDrawer || !isAddForm(form)) return 'pass';
  return form.getAttribute('aria-busy') === 'true' ? 'hold' : 'add';
}

// What a successful add shows (owner, 2026-10-28, one add-to-cart flow for paid ads): the drawer with its lines, free-shipping bar and Checkout,
// from every form (product page, sticky bar, card quick add, combo). The cart page already shows the cart, so there, and wherever the layout has
// no drawer, the toast confirms the add. This replaces the MoveArtly-only data-ns-open-cart opt-in, which the MoveArtly forms still carry.
export const afterAdd = ({ hasDialog, onCartPage } = {}) => (hasDialog && !onCartPage ? 'drawer' : 'toast');

// Shopify refuses an add or a change with { status, message, description }; show its description when it gives one.
// A network failure is a thrown Error whose message is browser jargon, so it gets the fallback.
export function errorText(json, fallback) {
  const text = json && typeof json === 'object' && !(json instanceof Error) ? json.description || json.message : null;
  return typeof text === 'string' && text.trim() ? text.trim() : fallback;
}

// /cart/add.js answers one item, or { items: [...] } when several were added at once.
export function addedItems(json) {
  if (!json || typeof json !== 'object') return [];
  if (Array.isArray(json.items)) return json.items;
  return json.id ? [json] : [];
}

// Shopify keeps only the stock it has when a change asks for more, without an error: report the quantity it kept.
export function cappedTo(cart, key, requested) {
  const line = cart && Array.isArray(cart.items) ? cart.items.find(item => item.key === key) : null;
  return line && requested > 0 && line.quantity < requested ? line.quantity : null;
}

// What a screen reader hears after a quantity change: the line's new quantity and the total, or that the line went.
export function statusText({ item, title, total }, { line, removed }) {
  return item ? fill(line, { title: item.product_title || title, count: item.quantity, total }) : fill(removed, { title, total });
}

// The toast's first line: a pre-order says how cancelling works; an in-stock item (MoveArtly pieces included) just says it
// was added, since 2026-10-27: the toast does not know the product's checked delivery line, which the cart line shows.
export const addedNote = (item, { stock, plan, moveartly }) => {
  if (item && item.selling_plan_allocation) return plan;
  if (item && moveartly && item.vendor === 'MoveArtly') return moveartly;
  return stock;
};

// "Add combo": the product as configured in its buy box, then each companion variant once.
export function comboItems(main, companions) {
  const items = [];
  if (main && Number(main.id)) items.push({ id: Number(main.id), quantity: 1, ...(Number(main.selling_plan) ? { selling_plan: Number(main.selling_plan) } : {}) });
  for (const id of companions) if (Number(id)) items.push({ id: Number(id), quantity: 1 });
  return items;
}

const root = () => (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const panels = () => [...document.querySelectorAll('[data-ns-cart-panel]')].map(node => node.dataset.nsCartPanel);
const drawer = () => document.querySelector('ns-cart-drawer');

async function post(path, body) {
  const json = !(body instanceof FormData);
  const response = await fetch(root() + path, { method: 'POST', headers: json ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' }, body: json ? JSON.stringify(body) : body });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw data || {};
  return data;
}

// Swap each cart panel (and the popup's amounts) for the freshly rendered section, then update the header badge.
function render(sections) {
  for (const [id, html] of Object.entries(sections || {})) {
    if (typeof html !== 'string') continue;
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    for (const [selector, scope] of [[`[data-ns-cart-panel="${CSS.escape(id)}"]`, document], ['[data-ns-help-amounts]', document]]) {
      const next = fresh.querySelector(selector);
      const here = scope.querySelector(selector);
      if (next && here) here.innerHTML = next.innerHTML;
    }
  }
  const count = Number(document.querySelector('[data-ns-cart-count]')?.dataset.nsCartCount);
  if (!Number.isFinite(count)) return;
  for (const link of document.querySelectorAll('[data-ns-cart-open][data-label]')) link.setAttribute('aria-label', fill(link.dataset.label, { count }));
  for (const badge of document.querySelectorAll('.ns-header__count')) {
    const grew = count > (Number(badge.textContent) || 0);
    badge.textContent = String(count);
    badge.hidden = count === 0;
    if (grew && moves()) badge.animate?.([{ transform: 'scale(1)' }, { transform: 'scale(1.45)' }, { transform: 'scale(1)' }], { duration: 420, easing: 'cubic-bezier(.2, .8, .2, 1)' });
  }
}

// Native feel (2026-10-23): the button that added turns green with a check for 1.6s (ns-base.css), then reads as before.
export const ADDED_MS = 1600;
const moves = () => !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
function markAdded(button) {
  if (!button || typeof button.setAttribute !== 'function') return;
  clearTimeout(button.nsAdded);
  button.setAttribute('data-ns-added', '');
  button.nsAdded = setTimeout(() => button.removeAttribute('data-ns-added'), ADDED_MS);
}

// Note toasts (07 Interactive Prototype flash(), 2.6s; motion audit fix 10): a save, an unsave (ns-saved.js announces
// ns:saved) and a watch (ns-watch.js sends ns:toast) get a short dark pill. The words live on the toast host.
export const NOTE_MS = 2600;
const NOTES = ['saved', 'unsaved', 'watching', 'unwatched'];
export function noteKey(detail) {
  if (!detail) return null;
  if ('saved' in detail) return detail.handle ? (detail.saved ? 'saved' : 'unsaved') : null;
  return NOTES.includes(detail.key) ? detail.key : null;
}

function toast(kind, values, ms = 3600) {
  // A modal drawer covers everything outside it, so while it is open the toast shows inside it.
  const host = document.querySelector('dialog.ns-cart[open] [data-ns-toasts-drawer]') || document.querySelector('[data-ns-toasts]');
  const template = document.querySelector(`template[data-ns-toast="${kind}"]`);
  if (!host || !template) return;
  const node = template.content.firstElementChild.cloneNode(true);
  for (const [slot, value] of Object.entries(values)) {
    const target = node.querySelector(`[data-slot="${slot}"]`);
    if (!target) continue;
    if (target.tagName === 'IMG') {
      if (value) target.src = value;
      else target.closest('[data-slot-wrap]')?.remove();
    } else {
      target.textContent = value ?? '';
    }
  }
  host.replaceChildren(node);
  let timer;
  const later = () => { timer = setTimeout(() => node.remove(), ms); };
  node.addEventListener('pointerenter', () => clearTimeout(timer));
  node.addEventListener('focusin', () => clearTimeout(timer));
  node.addEventListener('pointerleave', later);
  later();
}

function toastError(error, title) {
  const host = drawer();
  toast('error', { title: title || host?.dataset.errorTitle, sub: errorText(error, host?.dataset.errorText) });
}

function toastAdded(items) {
  const host = drawer();
  const item = items[0];
  if (!host || !item) return;
  const count = Number(document.querySelector('[data-ns-cart-count]')?.dataset.nsCartCount) || items.reduce((sum, line) => sum + (line.quantity || 0), 0);
  toast('added', {
    img: item.featured_image?.url || item.image || '',
    sub: addedNote(item, { stock: host.dataset.addedStock, plan: host.dataset.addedPlan, moveartly: host.dataset.addedMoveartly }),
    title: item.product_title || item.title || '',
    cta: fill(host.dataset.cartLabel, { count }),
  });
}

async function add(body, busy, button = busy?.querySelector?.('[type="submit"]')) {
  busy?.setAttribute('aria-busy', 'true');
  try {
    if (body instanceof FormData) {
      body.append('sections', panels().join(','));
      body.append('sections_url', window.location.pathname);
    } else {
      body.sections = panels().join(',');
      body.sections_url = window.location.pathname;
    }
    const json = await post('cart/add.js', body);
    render(json.sections);
    markAdded(button);
    // The drawer was just rendered from this add's sections, so it opens without fetching them again (unless Shopify left its section out).
    // An add from inside the open drawer (the gift, a popular item) re-rendered the button away, so the toast in the drawer still speaks.
    const drawerHtml = typeof json.sections?.[document.querySelector('ns-cart-drawer [data-ns-cart-panel]')?.dataset.nsCartPanel] === 'string';
    if (afterAdd({ hasDialog: Boolean(document.getElementById('NsCart')), onCartPage: Boolean(document.querySelector('.ns-cart-page')) }) !== 'drawer' || !open({ fresh: drawerHtml })) toastAdded(addedItems(json));
  } catch (error) {
    toastError(error);
  } finally {
    busy?.removeAttribute('aria-busy');
  }
}

// After a re-render, focus returns to the same control on the same line, or to the panel when the line is gone.
function refocus(key, step, panel) {
  const line = [...document.querySelectorAll('.ns-cart-line')].find(node => node.dataset.key === key);
  const target = line?.querySelector(`[data-ns-step="${step}"]`) || document.querySelector(`[data-ns-cart-panel="${panel}"]`);
  target?.focus();
}

function announce(json, key, title) {
  const region = document.querySelector('dialog.ns-cart[open] [data-ns-cart-status]') || document.querySelector('.ns-cart-page ~ [data-ns-cart-status], .ns-cart-page [data-ns-cart-status]');
  if (!region) return;
  const item = Array.isArray(json?.items) ? json.items.find(entry => entry.key === key) : null;
  const total = document.querySelector('.ns-cart-summary__total span:last-child')?.textContent.trim() ?? '';
  region.textContent = statusText({ item, title, total }, { line: region.dataset.line, removed: region.dataset.removed });
}

async function change(key, quantity, line, step, panel) {
  const title = line?.querySelector('.ns-cart-line__title')?.textContent.trim() ?? '';
  line?.setAttribute('aria-busy', 'true');
  try {
    const json = await post('cart/change.js', { id: key, quantity, sections: panels().join(','), sections_url: window.location.pathname });
    render(json.sections);
    refocus(key, step, panel);
    announce(json, key, title);
    const kept = cappedTo(json, key, quantity);
    if (kept !== null) toast('error', { title: drawer()?.dataset.changeTitle, sub: fill(drawer()?.dataset.capped, { count: kept }) });
  } catch (error) {
    line?.removeAttribute('aria-busy');
    toastError(error, drawer()?.dataset.changeTitle);
  }
}

// The drawer was rendered with the page; another tab or the back button may have changed the cart since, so it re-renders on open.
async function refresh(dialog) {
  try {
    const response = await fetch(`${root()}?sections=${encodeURIComponent(panels().join(','))}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    render(await response.json());
    if (dialog.open && !dialog.contains(document.activeElement)) dialog.querySelector('.ns-dialog__close')?.focus();
  } catch {
    // Offline: the drawer keeps what the page rendered.
  }
}

function open({ fresh = false } = {}) {
  const dialog = document.getElementById('NsCart');
  if (!dialog || dialog.open) return false;
  dialog.showModal();
  dialog.querySelector('.ns-dialog__close')?.focus();
  if (!fresh) refresh(dialog);
  return true;
}

if (typeof customElements !== 'undefined' && !customElements.get('ns-cart-drawer')) {
  customElements.define('ns-cart-drawer', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog.ns-cart');
      // A click on the backdrop lands on the dialog itself: close it.
      this.onBackdrop = event => { if (event.target === this.dialog) this.dialog.close(); };
      this.dialog?.addEventListener('click', this.onBackdrop);
    }
    disconnectedCallback() {
      this.dialog?.removeEventListener('click', this.onBackdrop);
    }
  });

  const note = event => {
    const key = noteKey(event.detail);
    const text = key ? document.querySelector('[data-ns-toasts]')?.dataset[key] : '';
    if (text) toast('note', { title: text }, NOTE_MS);
  };
  document.addEventListener('ns:saved', note);
  document.addEventListener('ns:toast', note);

  // Shopify's shop_events_listener binds its own submit handler on every /cart/add form and records "Added Product" unless
  // the submit is already prevented; its fetch wrapper then records the same add again from the /cart/add.js answer. A
  // prevent in the bubbling handler below comes too late for the first, so one add went to Meta, Google and GA4 twice
  // (MoveArtly audit, 2026-10-28). Preventing in the capture phase leaves one product_added_to_cart, the one from the
  // cart's answer (real line price), whose Shopify event id the pixels and the channels' server events share. The add
  // itself stays in the bubbling handler, after the form's own submit listeners (ma-product's gift sync) have run.
  document.addEventListener('submit', event => {
    if (submitDecision(event.target, Boolean(drawer())) !== 'pass') event.preventDefault();
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    const decision = submitDecision(form, Boolean(drawer()));
    if (decision === 'pass') return;
    event.preventDefault();
    if (decision === 'add') add(new FormData(form), form, event.submitter || undefined);
  });

  document.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const step = target.closest('[data-ns-cart-line]');
    if (step && drawer()) {
      event.preventDefault();
      change(step.dataset.nsCartLine, Number(step.dataset.quantity), step.closest('.ns-cart-line'), step.dataset.nsStep, step.closest('[data-ns-cart-panel]')?.dataset.nsCartPanel);
      return;
    }
    const opener = target.closest('[data-ns-cart-open]');
    if (opener && !document.querySelector('.ns-cart-page')) {
      if (open()) event.preventDefault();
      target.closest('.ns-toast')?.remove();
      return;
    }
    if (target.closest('[data-ns-help-open]')) {
      document.getElementById('NsPreorderHelp')?.showModal();
      return;
    }
    if (target.closest('[data-ns-toast-close]')) {
      target.closest('.ns-toast')?.remove();
      return;
    }
    const combo = target.closest('[data-ns-combo-add]');
    if (combo && drawer()) {
      if (combo.getAttribute('aria-busy') === 'true') return;
      const form = document.querySelector('form.ns-buy__form');
      const fields = form ? Object.fromEntries(new FormData(form)) : null;
      const blocked = !form || form.querySelector('.ns-buy__cta')?.disabled;
      if (blocked) return toast('error', { title: drawer().dataset.errorTitle, sub: combo.dataset.blocked });
      add({ items: comboItems(fields, (combo.dataset.items || '').split(',')) }, combo, combo);
    }
  });
}
